import React from 'react';
import { Search } from 'lucide-react';

// Plank zoom component — uses background-image for reliable zoom in constrained layouts
const PlankZoomImage = ({ src, alt }) => {
  const [isHovering, setIsHovering] = React.useState(false);
  const [isLocked, setIsLocked] = React.useState(false);
  const [cursorPos, setCursorPos] = React.useState({ x: 0.5, y: 0.5 });
  const [isOverImage, setIsOverImage] = React.useState(false);
  const [imgLoaded, setImgLoaded] = React.useState(false);
  const containerRef = React.useRef(null);
  const imgRef = React.useRef(null);

  const LENS_SIZE = 180;
  const ZOOM_LEVEL = 3;

  React.useEffect(() => {
    setIsLocked(false);
    setIsHovering(false);
    setImgLoaded(false);
    setIsOverImage(false);
  }, [src]);

  const isTouchDevice = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

  // Calculate the actual rendered image area within the object-contain img element
  const getRenderedBounds = React.useCallback(() => {
    if (!imgRef.current) return null;
    const img = imgRef.current;
    const elemRect = img.getBoundingClientRect();
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    if (!nw || !nh) return null;

    const scaleW = elemRect.width / nw;
    const scaleH = elemRect.height / nh;
    const scale = Math.min(scaleW, scaleH);

    const renderedW = nw * scale;
    const renderedH = nh * scale;
    const offsetX = (elemRect.width - renderedW) / 2;
    const offsetY = (elemRect.height - renderedH) / 2;

    return {
      left: elemRect.left + offsetX,
      top: elemRect.top + offsetY,
      width: renderedW,
      height: renderedH,
    };
  }, []);

  const updateFromClient = React.useCallback((clientX, clientY) => {
    const bounds = getRenderedBounds();
    if (!bounds) return;
    const overImg = clientX >= bounds.left && clientX <= bounds.left + bounds.width &&
                    clientY >= bounds.top && clientY <= bounds.top + bounds.height;
    setIsOverImage(overImg);
    if (overImg || isLocked) {
      const x = Math.max(0, Math.min(1, (clientX - bounds.left) / bounds.width));
      const y = Math.max(0, Math.min(1, (clientY - bounds.top) / bounds.height));
      setCursorPos({ x, y });
    }
  }, [isLocked, getRenderedBounds]);

  const handleMouseMove = React.useCallback((e) => {
    if (isTouchDevice) return;
    updateFromClient(e.clientX, e.clientY);
  }, [isTouchDevice, updateFromClient]);

  const handleClick = React.useCallback((e) => {
    if (isTouchDevice) return;
    e.stopPropagation();
    if (isLocked) {
      setIsLocked(false);
    } else if (isOverImage) {
      setIsLocked(true);
    }
  }, [isTouchDevice, isLocked, isOverImage]);

  const handleTouchStart = React.useCallback((e) => {
    if (e.touches.length === 1) {
      updateFromClient(e.touches[0].clientX, e.touches[0].clientY);
      if (!isLocked) setIsLocked(true);
    }
  }, [isLocked, updateFromClient]);

  const handleTouchMove = React.useCallback((e) => {
    if (!isLocked || e.touches.length !== 1) return;
    e.preventDefault();
    updateFromClient(e.touches[0].clientX, e.touches[0].clientY);
  }, [isLocked, updateFromClient]);

  const getLensStyle = React.useCallback(() => {
    const bounds = getRenderedBounds();
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!bounds || !containerRect) return {};

    const half = LENS_SIZE / 2;

    // Lens center in container coordinates
    const lensX = (bounds.left - containerRect.left) + cursorPos.x * bounds.width;
    const lensY = (bounds.top - containerRect.top) + cursorPos.y * bounds.height;

    // Zoomed image dimensions
    const zoomedW = bounds.width * ZOOM_LEVEL;
    const zoomedH = bounds.height * ZOOM_LEVEL;

    return {
      left: `${lensX}px`,
      top: `${lensY}px`,
      width: `${LENS_SIZE}px`,
      height: `${LENS_SIZE}px`,
      backgroundImage: `url(${src})`,
      backgroundSize: `${zoomedW}px ${zoomedH}px`,
      backgroundPosition: `${-(cursorPos.x * zoomedW - half)}px ${-(cursorPos.y * zoomedH - half)}px`,
    };
  }, [cursorPos, src, getRenderedBounds]);

  const showLens = imgLoaded && ((isHovering && isOverImage && !isTouchDevice) || isLocked);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 flex items-center justify-center bg-muted overflow-hidden"
      onMouseEnter={() => { if (!isTouchDevice) setIsHovering(true); }}
      onMouseLeave={() => { if (!isTouchDevice) { setIsHovering(false); if (!isLocked) { setIsLocked(false); setIsOverImage(false); } } }}
      onMouseMove={handleMouseMove}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      style={{ cursor: isOverImage ? (isLocked ? 'crosshair' : 'zoom-in') : 'default', touchAction: isLocked ? 'none' : 'auto' }}
      data-testid="plank-zoom-container"
    >
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className="w-full h-full object-contain select-none pointer-events-none"
        onLoad={() => setImgLoaded(true)}
        draggable={false}
      />

      {/* Magnifying lens */}
      {showLens && (
        <div
          className="absolute rounded-full pointer-events-none z-20"
          style={{
            ...getLensStyle(),
            transform: 'translate(-50%, -50%)',
            backgroundRepeat: 'no-repeat',
            border: '3px solid rgba(255,255,255,0.9)',
            boxShadow: '0 0 0 1px rgba(0,0,0,0.2), 0 8px 32px rgba(0,0,0,0.35)',
          }}
          data-testid="plank-zoom-lens"
        />
      )}

      {/* Zoom hint */}
      {!showLens && imgLoaded && (
        <div className="absolute bottom-4 right-4 flex items-center gap-1.5 px-3 py-1.5 bg-black/50 backdrop-blur-sm rounded-full text-white text-xs font-medium pointer-events-none select-none z-10"
          data-testid="plank-zoom-hint">
          <Search className="w-3 h-3" />
          {isTouchDevice ? 'Tap to inspect' : 'Hover to inspect · Click to lock'}
        </div>
      )}

      {/* Locked indicator */}
      {isLocked && (
        <button
          onClick={(e) => { e.stopPropagation(); setIsLocked(false); }}
          className="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 bg-primary/90 backdrop-blur-sm rounded-full text-white text-xs font-medium z-20 hover:bg-primary transition-colors"
          data-testid="plank-zoom-unlock"
        >
          <Search className="w-3 h-3" />
          {ZOOM_LEVEL}x Locked — Click to release
        </button>
      )}
    </div>
  );
};

export default PlankZoomImage;
